import { WeddingTableItem } from '@root/sanity/sanity.types';
import IconCheck from 'components/IconCheck';
import IconX from 'components/IconX';
import { ReactElement } from 'react';

type WeddingTableProps = {
  items: WeddingTableItem[];
  content?: ReactElement;
  backgroundColor?: string;
};

type GroupedWeddingItems = WeddingTableItem[][];

const checkGroupItemBorder = (groupItem: WeddingTableItem[]) => groupItem.every((item) => item?.border);

//Grouping the items as border/no border sub-arrays. This is so we can figure out which statements, if any, gets the borders.
const getItemsGrouped = (items: WeddingTableItem[]): GroupedWeddingItems => {
  let groupCounter = 0;
  return items.reduce(
    (acc: GroupedWeddingItems, currItem: WeddingTableItem, itemIndex: number) => {
      if (groupCounter === 0 && itemIndex === 0) {
        //initial
        acc[groupCounter].push(currItem);
      } else {
        const prevItem = items[itemIndex - 1];
        if (currItem?.border && prevItem?.border) {
          acc[groupCounter].push(currItem);
        } else {
          //This is a new group
          groupCounter++;
          acc[groupCounter] = [currItem];
        }
      }

      return acc;
    },
    [[]],
  );
};

const WeddingTable = ({ items, content, backgroundColor }: WeddingTableProps) => {
  const bucketedItems = items ? getItemsGrouped(items) : [];

  return (
    <div className={`${backgroundColor ?? 'bg-gray-lighter'} py-1`}>
      <div className="m-section container">
        {content && (
          <div className="row mb-64">
            <div className="col-span-12 sm:col-span-6 sm:col-start-4">{content}</div>
          </div>
        )}

        <div className="row sm:pt-32">
          <div className="hidden lg:col-span-1 lg:block" />

          <div className="col-span-12 lg:col-span-10">
            <table className="w-full border-collapse">
              <tbody>
                <tr>
                  <th className="px-8 py-32 xs:px-16 xs:pl-32" />

                  <th className="bg-white px-8 py-32 font-normal xs:px-16">
                    <span className="text-h3-display block">Wedding Couple</span>
                  </th>

                  <th className="px-8 py-32 xs:px-16">
                    <span className="text-h5 block text-gray-darker">The Wedding Party</span>
                  </th>
                </tr>

                {bucketedItems.map((groupItem) =>
                  groupItem.map((item, itemIndex) => (
                    <tr className={`${checkGroupItemBorder(groupItem) && itemIndex === 0 ? 'border-t' : ''} border-gray-light`}>
                      <td
                        className={`${checkGroupItemBorder(groupItem) && itemIndex === groupItem.length - 1 ? 'border-b' : ''} ${
                          checkGroupItemBorder(groupItem) ? 'border-l' : ''
                        } border-gray-light px-8 py-8 pt-32 xs:px-16 xs:py-16`}
                      >
                        <p className="text-sm text-gray-darker sm:text-base">{item.statement}</p>
                      </td>

                      <td
                        className={`${
                          checkGroupItemBorder(groupItem) && itemIndex === groupItem.length - 1 ? 'border-b' : ''
                        } border-gray-light bg-white px-8 py-8 pt-32 xs:px-16 xs:py-16`}
                      >
                        <div
                          className={`m-auto flex h-32 w-32 items-center justify-center rounded-full border ${
                            item?.weddingCouple ? 'border-gold bg-white' : 'border-gray-light bg-gray-light'
                          }`}
                        >
                          {item?.weddingCouple ? <IconCheck /> : <IconX />}
                        </div>
                      </td>

                      <td
                        className={`${checkGroupItemBorder(groupItem) && itemIndex == groupItem.length - 1 ? 'border-b' : ''} ${
                          checkGroupItemBorder(groupItem) ? 'border-r' : ''
                        } border-gray-light px-8 py-8 pt-32 xs:px-16 xs:py-16`}
                      >
                        <div
                          className={`m-auto flex h-32 w-32 items-center justify-center rounded-full border ${
                            item?.weddingParty ? 'border-gold bg-white' : 'border-gray-light bg-gray-light'
                          }`}
                        >
                          {item?.weddingParty ? <IconCheck /> : <IconX />}
                        </div>
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeddingTable;
